import { cloneDeep } from "lodash";
import { replace } from "redux-first-history";
import { channel, type Channel } from "redux-saga";
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
  type SagaReturnType,
} from "redux-saga/effects";

import { iotEventAssetUpdate } from "@ds/modules/iot/redux/actions";
import { toastShowErrorAction, toastShowSuccessAction } from "@ds/modules/notifications/redux/actions";
import { isIotTableEntityAddedOrDeleted, selectTableData } from "@ds/modules/table-data/redux/selectors";
import { convertToApiQueryInfo } from "@ds/modules/table-data/utils/common";

import { storageService, type ProgressInfo } from "@ds/services/storage";
import { UnexpectedError } from "@ds/utils/errors";
import { logger } from "@ds/utils/logger";
import { UNLIMITED_PAGINATION } from "@ds/utils/query";
import { takeLatestOrEvery } from "@ds/utils/saga-helpers";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { createAssetModel } from "../../utils/model";
import { assetsService } from "../../utils/assets-api";
import {
  createAssetFiltersByTableType,
  getMainTableTypeByApiContentType,
  getMainTableTypeByContentType,
} from "../../utils/helpers";
import { normalize } from "../../utils/normalizer";
import { selectAssetById, selectTableAssets } from "../selectors/common-selectors";
import { assetsActions, AssetsRemoteOperation, type AssetsProgressChannelEvent } from "../slice";

function* fetchAssets({ meta }: ReturnType<typeof assetsActions.fetchAssets>) {
  try {
    const { sorting, pagination, queryInfo, isFetchedAlready } = selectTableData(
      yield select(),
      meta.options.tableType || getMainTableTypeByApiContentType(meta.filters?.contentType),
      undefined,
      meta.filters,
    );

    const newMeta = cloneDeep(meta);
    const resultFromCache: QueryOutput<Asset> = { items: [] };

    if (isFetchedAlready && newMeta.options.tableType && !newMeta.options.cache?.disableCache) {
      resultFromCache.items = selectTableAssets(yield select(), newMeta.options.tableType);
      newMeta.options.cache = {
        ...newMeta.options.cache,
        fetchedFromCache: true,
      };
    }

    let result: QueryOutput<Asset> = newMeta.options.cache?.fetchedFromCache
      ? resultFromCache
      : yield call(
          [assetsService, assetsService.getAssets],
          convertToApiQueryInfo(sorting, queryInfo),
          newMeta.options.tableType ? pagination : UNLIMITED_PAGINATION,
        );

    result = newMeta.options.cache?.fetchedFromCache ? result : normalize(result);
    result = newMeta.options.tableType ? result : { items: result.items };

    yield put(assetsActions.fetchAssetsSucceeded(result, newMeta));
  } catch (err) {
    const errorTitle = "Fetch assets";
    if (isErrorLike(err)) {
      yield put(assetsActions.fetchAssetsFailed(meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* fetchAsset({ payload, meta }: ReturnType<typeof assetsActions.fetchAsset>) {
  try {
    const newMeta = cloneDeep(meta);
    let result = selectAssetById(yield select(), payload);
    if (result && !newMeta.options.cache?.disableCache) {
      newMeta.options.cache = {
        ...newMeta.options.cache,
        fetchedFromCache: true,
      };
    } else {
      result = yield call([assetsService, assetsService.getAsset], payload);
      result = normalize(result);
    }

    yield put(assetsActions.fetchAssetSucceeded(result, newMeta));
  } catch (err) {
    const errorTitle = "Fetch asset";
    if (isErrorLike(err)) {
      yield put(assetsActions.fetchAssetFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* deleteAssets({ payload, meta }: ReturnType<typeof assetsActions.deleteAssets>) {
  try {
    yield call([assetsService, assetsService.deleteAssets], payload);
    yield put(assetsActions.deleteAssetsSucceeded(payload, meta));
    yield put(toastShowSuccessAction("Asset(s) were deleted successfully"));

    if (meta.options?.redirectTo) {
      yield put(replace(meta.options.redirectTo));
    }
  } catch (err) {
    const errorTitle = "Delete asset(s)";
    if (isErrorLike(err)) {
      yield put(assetsActions.deleteAssetsFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

export function* createAsset(progressChannel: Channel<AssetsProgressChannelEvent>, file: File) {
  const { sorting, queryInfo } = selectTableData(yield select(), getMainTableTypeByContentType(file.type), undefined, {
    name: file.name,
  });

  const existingAssets: SagaReturnType<typeof assetsService.getAssets> = yield call(
    [assetsService, assetsService.getAssets],
    convertToApiQueryInfo(sorting, queryInfo),
    UNLIMITED_PAGINATION,
  );

  if (existingAssets.items.length) {
    throw new Error(`Asset with name ${file.name} already exists`);
  }

  const uniqueKey = `assets/${new Date().getTime()}_${file.name}`;
  const { key }: SagaReturnType<typeof storageService.put> = yield call(
    [storageService, storageService.put],
    uniqueKey,
    file,
    {
      level: "public",
      contentType: file.type,
      progressCallback: (progressInfo: ProgressInfo) => {
        progressChannel.put({ progressInfo: { ...progressInfo, name: file.name } });
      },
    },
  );

  const result: SagaReturnType<typeof assetsService.createAsset> = yield call(
    [assetsService, assetsService.createAsset],
    createAssetModel({
      key,
      name: file.name,
      file_name: file.name,
      file_size: file.size,
      content_type: file.type,
    }),
  );

  return result;
}

function* uploadAsset(
  progressChannel: Channel<AssetsProgressChannelEvent>,
  { payload, meta }: ReturnType<typeof assetsActions.uploadAsset>,
) {
  try {
    const result: SagaReturnType<typeof createAsset> = yield call(createAsset, progressChannel, payload);

    yield put(assetsActions.uploadAssetSucceeded(normalize(result), meta));
    yield put(toastShowSuccessAction("Asset was successfully uploaded"));
  } catch (err) {
    const errorTitle = "Upload asset";
    if (isErrorLike(err)) {
      yield put(assetsActions.uploadAssetFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* uploadAssets(
  progressChannel: Channel<AssetsProgressChannelEvent>,
  { payload, meta }: ReturnType<typeof assetsActions.uploadAssets>,
) {
  yield all(
    payload.map(entity =>
      call(
        uploadAsset,
        progressChannel,
        assetsActions.uploadAsset(entity, getMainTableTypeByApiContentType(meta.filters?.contentType)),
      ),
    ),
  );
}

function* refetchAssets({ payload }: ReturnType<typeof iotEventAssetUpdate>) {
  const tableType = getMainTableTypeByContentType(payload.content_type);
  const isAddedOrDeleted = isIotTableEntityAddedOrDeleted(yield select(), payload, tableType);
  if (isAddedOrDeleted) {
    yield call(
      fetchAssets,
      assetsActions.fetchAssets(
        createAssetFiltersByTableType({ tableType }),
        { tableType, cache: { disableCache: true } },
        AssetsRemoteOperation.IOT_FETCH,
      ),
    );
  }

  yield put(assetsActions.iotUpdate(payload));
}

function* handleUploading() {
  const progressChannel: Channel<AssetsProgressChannelEvent> = yield call(channel);
  yield takeEvery(assetsActions.uploadAssets, uploadAssets, progressChannel);

  while (true) {
    const { progressInfo }: AssetsProgressChannelEvent = yield take(progressChannel);
    yield put(assetsActions.progressUploadingAsset(progressInfo));
  }
}

export function* watchAssets() {
  yield takeLatestOrEvery(assetsActions.fetchAssets, function* (action) {
    yield race({
      task: call(fetchAssets, action),
      cancel: take([assetsActions.selectAssets, assetsActions.toggleSelected]),
    });
  });

  yield takeLatestOrEvery(assetsActions.fetchAsset, fetchAsset);
  yield takeLeading(assetsActions.deleteAssets, deleteAssets);
  yield takeLatest(iotEventAssetUpdate, refetchAssets);

  yield call(handleUploading);
}
